<template>
	<div
		v-if="items.length > 1"
		v-click-outside="closeLangNav"
		class="lang-select"
		:class="[color, { 'fullpage-nav-active': fullpageNavActive }]"
		@click="toggleLangNav"
	>
		<div class="lang-select-container">
			<div class="active-wrapper">
				<span>{{ locale }}&nbsp;</span>
				<img class="icon-arrow" src="~/assets/images/lang-nav-down.svg" alt="Select a language" />
			</div>
			<div v-if="showLangNav" class="lang-wrapper" :class="[color, { compact }]">
				<div
					v-for="item in items.filter((lang) => lang.language !== locale)"
					:key="`${item.filename}-${item.language}`"
				>
					<nuxt-link :class="item.language" :to="`${item.filename}`" @click="hideMenu">
						{{ item.language }}
					</nuxt-link>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup>
const { locale } = useI18n();

defineProps({
	color: { type: String, default: 'light' },
	compact: { type: Boolean, default: false },
	items: { type: Array, default: () => [] },
	fullpageNavActive: { type: Boolean, default: false },
});

const showLangNav = ref(false);

const toggleLangNav = () => {
	showLangNav.value = !showLangNav.value;
};

const closeLangNav = () => {
	showLangNav.value = false;
};

onMounted(() => {
	document.addEventListener('scroll', closeLangNav);
});

onBeforeUnmount(() => {
	document.removeEventListener('scroll', closeLangNav);
});
</script>

<style lang="scss" scoped>
.lang-select {
	max-width: 110px;
	padding: 12px 15px;
	cursor: pointer;
	position: relative;
	text-transform: uppercase;
	display: flex;
	align-items: center;
	font-family: var(--button-font-family);
	letter-spacing: 2px;
	font-weight: 700;
	color: var(--nav-color-light);
	transition: color 0.3s ease;

	.lang-select-container {
		display: flex;
		align-items: center;

		.active-wrapper {
			display: flex;
			flex-flow: row nowrap;
			align-items: center;
		}
	}

	span {
		font-size: 15px;
		margin: 0 5px 0 0;
	}

	.icon-arrow {
		width: 23px;
		height: 8px;
		transition: all 0.3s ease-in-out;
	}

	&.dark {
		color: var(--cta-color-hover);

		.icon-arrow {
			filter: brightness(0) saturate(100%) invert(5%) sepia(68%) saturate(1091%) hue-rotate(332deg)
				brightness(81%) contrast(100%);
		}
	}

	&.medium {
		color: var(--nav-color-dark);

		&:hover {
			color: var(--nav-color-dark);

			.icon-arrow {
				filter: brightness(0) saturate(100%) invert(100%) sepia(0%) saturate(0%) hue-rotate(342deg)
					brightness(106%) contrast(106%);
			}
		}

		.icon-arrow {
			filter: brightness(0) saturate(100%) invert(74%) sepia(6%) saturate(977%) hue-rotate(326deg) brightness(94%)
				contrast(80%);
		}
	}
}

.scroll-up {
	.lang-select {
		.lang-select-container {
			flex-flow: column wrap;
			justify-content: flex-start;
			align-items: flex-start;

			.lang-wrapper.dark {
				padding: 0 0 10px;
			}
		}
	}
}

.lang-wrapper {
	position: absolute;
	top: 37px;
	left: 5px;
	right: 3px;
	padding: 0 9px 10px;
	text-align: center;
	z-index: 90;
	font-size: 24px;
	letter-spacing: 3px;
	width: 38px;

	a {
		display: flex;
		flex-flow: column nowrap;
		align-items: center;
		place-content: space-between flex-start;
		color: var(--nav-color-light);
		gap: 5px;
		font-size: 15px;
		width: 100%;
		flex-direction: row;
		user-select: none;
		margin: 3px 0;

		&:hover {
			color: #fff;
		}
	}

	&.compact {
		position: relative;
		width: 100%;
		inset: auto;
	}

	&.dark {
		background: none;

		a {
			color: var(--cta-color-hover);

			&:hover {
				color: #777;
			}
		}
	}

	&.medium {
		background: var(--alternating-background-color);
		padding: 0 25px 10px;
		width: 60px;
		margin-left: -15.5px;

		a {
			color: var(--nav-color-dark);

			&:hover {
				color: #fff;
			}
		}
	}
}

@media (max-width: 920px) {
	.lang-select {
		display: none;
		position: absolute;
		width: auto;
		left: 90px;
		text-align: center;
		max-width: none;
		justify-content: center;
		z-index: 1;

		&.fullpage-nav-active {
			display: flex;
		}

		.lang-select-container {
			position: relative;
			width: auto;
			text-align: center;
			display: flex;
			align-items: center;
		}
	}

	.lang-wrapper {
		top: 25px;
		left: -10px;
		width: 70vw;

		&.medium {
			width: 70vw;
			padding-bottom: 35px;
			background: var(--dark-background-color);
		}
	}
}
</style>
